import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { getAuth } from 'firebase/auth';
import {
  subscribeExclusiveLicenseNotifications,
  dismissExclusiveLicenseNotification,
  getNotificationPreferences,
  ExclusiveLicenseNotification,
} from '../../services/notification.service';

interface ExclusiveLicenseNotificationModalProps {
  firebase: any;
}

const getSessionKey = (orgId: string) => `licenseNotifShown_${orgId}`;

const ExclusiveLicenseNotificationModal: React.FC<ExclusiveLicenseNotificationModalProps> = ({ firebase }) => {
  const [notifications, setNotifications] = useState<ExclusiveLicenseNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  const { currentOrgId, currentOrgName } = useOrganizationStore();

  // Check for notifications when org changes (only if user has licenseExpiry notifications enabled)
  // Only auto-open once per login session per org
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setNotifications([]);
      return;
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    // Gate the live subscription on the user's licenseExpiry preference.
    (async () => {
      try {
        const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
        if (cancelled) return;
        if (!prefs.inApp.exclusiveLicenseEndings) {
          setNotifications([]);
          return;
        }

        setLoading(true);
        unsubscribe = subscribeExclusiveLicenseNotifications(firebase, currentOrgId, (result) => {
          setNotifications(result);
          setLoading(false);

          // Only auto-open once per session per org
          const sessionKey = getSessionKey(currentOrgId);
          if (!sessionStorage.getItem(sessionKey) && result.length > 0) {
            setIsOpen(true);
            sessionStorage.setItem(sessionKey, 'true');
          }
        });
      } catch (error) {
        console.error('Error setting up exclusive license notifications subscription:', error);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentOrgId, firebase, setIsOpen]);

  const handleRepublish = async (notification: ExclusiveLicenseNotification) => {
    if (!firebase || !currentOrgId) return;

    setProcessingIds((prev) => new Set(prev).add(notification.id));

    try {
      // Get the track data to republish
      const trackDoc = await firebase.getOrgAudioMetaData(currentOrgId, notification.trackId);
      if (trackDoc.exists()) {
        const trackData = trackDoc.data();

        // Republish to public catalogue
        await firebase.publishToPublicCatalogue(currentOrgId, currentOrgName, notification.trackId, trackData);

        // Unhide the track (in case it was hidden)
        await firebase.setTrackHiddenByExclusiveLicense(notification.trackId, false, null);
      }

      // Dismiss the notification with republish flag — subscription updates the list.
      await dismissExclusiveLicenseNotification(firebase, {
        orgId: currentOrgId,
        notificationId: notification.id,
        republished: true,
      });
    } catch (error) {
      console.error('Error republishing track:', error);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleDismiss = async (notification: ExclusiveLicenseNotification) => {
    if (!firebase || !currentOrgId) return;

    setProcessingIds((prev) => new Set(prev).add(notification.id));

    try {
      // Just dismiss without republishing
      await dismissExclusiveLicenseNotification(firebase, {
        orgId: currentOrgId,
        notificationId: notification.id,
        republished: false,
      });

      // Unhide from public catalogue but don't republish
      await firebase.setTrackHiddenByExclusiveLicense(notification.trackId, false, null);
      // Subscription removes the notification from the list once Firestore mutates.
    } catch (error) {
      console.error('Error dismissing notification:', error);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notification.id);
        return newSet;
      });
    }
  };

  const handleRemindLater = () => {
    setIsOpen(false);
  };

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && notifications.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [notifications.length, isOpen, loading]);

  if (!isOpen || notifications.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleRemindLater}
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleRemindLater}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleRemindLater}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Exclusive License Ended ({notifications.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              The following tracks had exclusive licenses that have ended. Would you like to make them public again?
            </p>
            <div className="exclusive-notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    marginBottom: '0.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        marginBottom: '0.25rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {notification.trackName || 'Unknown Track'}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      {notification.type === 'revoked' ? 'License revoked' : 'License expired'}
                      {notification.clientName && ` | Client: ${notification.clientName}`}
                      {' | '}
                      {moment(notification.endedAt).format('DD/MM/YYYY')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <Button
                      size="sm"
                      className="btn-primary-custom"
                      onClick={() => handleRepublish(notification)}
                      disabled={processingIds.has(notification.id)}
                      style={{ minWidth: '90px' }}
                    >
                      {processingIds.has(notification.id) ? (
                        <Spinner size="sm" />
                      ) : (
                        <span style={{ position: 'relative', zIndex: 2 }}>Make Public</span>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      className="btn-secondary-custom"
                      onClick={() => handleDismiss(notification)}
                      disabled={processingIds.has(notification.id)}
                      style={{ minWidth: '70px' }}
                    >
                      {processingIds.has(notification.id) ? (
                        <Spinner size="sm" />
                      ) : (
                        'Dismiss'
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleRemindLater}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExclusiveLicenseNotificationModal;
